import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useNotifier } from "@repo/shared/common";

import type { CreditNote } from "../types/CommercialDocumentTypes";
import { ModifyInvoiceItemInput, useUpdateDocumentDraftMutation } from "@repo/server-api";

type UseUpdateCreditNoteOptions = {
	onSaved: (creditNote: CreditNote) => void;
};

type UseUpdateCreditNoteResult = {
	updateCreditNote: (creditNote: CreditNote) => void;
	loading: boolean;
};

export function useUpdateCreditNoteHook(
	options: UseUpdateCreditNoteOptions,
): UseUpdateCreditNoteResult {
	const { t } = useTranslation();
	const { notifySuccess, notifyException } = useNotifier();

	const { onSaved } = options;
	const [loading, setLoading] = useState(false);

	const [updateDocumentDraftMutation] = useUpdateDocumentDraftMutation();

	const updateCreditNote = async (creditNote: CreditNote) => {
		setLoading(true);
		try {
			await updateDocumentDraftMutation({
				variables: {
					documentId: creditNote.id!,
					settlesInvoiceId: creditNote.settlesInvoiceId ?? null,
					clientContactPersonId: creditNote.contactPerson?.id || null,
					clientId: creditNote.client?.id || null,
					concludingText: creditNote.footerNote,
					contractorId: creditNote.contractor?.id || null,
					introductoryText: creditNote.headerNote || "",
					items: creditNote.items.map((item, index) => ({
						description: item.description,
						discountPercent: item.discount ?? 0,
						name: item.name,
						quantity: item.quantity,
						tax: item.tax,
						taxCode: item.taxCode,
						unit: item.unit,
						unitPriceNet: item.unitPrice,
						positionNo: index,
					})) as ModifyInvoiceItemInput[],
					notes: creditNote.notes || "",
					paymentDeadlineInDays: creditNote.paymentDeadlineDays || 30,
					projectId: creditNote.project?.id || null,
					isProforma: creditNote.isProforma || false,
				},
			});

			notifySuccess(t("credit-note-updated"));
			onSaved(creditNote);
		} catch (error) {
			notifyException(error);
		} finally {
			setLoading(false);
		}
	};

	return {
		updateCreditNote,
		loading,
	};
}
